import { QueryClient, QueryKey } from 'react-query'
import { AlmanacFileCalendarItem } from './types'
import { calendarKeys } from './calendarKeys'
import dayjs from 'dayjs'
import { CalendarFilters } from 'screens/Calendar/types'
import { BrandPlanSchedule } from 'types'
import { filesKeys } from '../files/filesKeys'
import { AlmanacFileIndexItem } from '../files/types'

interface QueryDateRange {
  startDate: string
  endDate: string
}

interface MutationContext {
  previousData: { [key: string]: AlmanacFileCalendarItem[] }
}

// Common utility to store previous state and update query data
const updateQueryData = (
  queryClient: QueryClient,
  queryKey: QueryKey,
  newData: AlmanacFileCalendarItem[],
  previousData: { [key: string]: AlmanacFileCalendarItem[] },
) => {
  const keyString = JSON.stringify(queryKey)
  const currentData = queryClient.getQueryData<AlmanacFileCalendarItem[]>(queryKey) || []
  previousData[keyString] = currentData
  queryClient.setQueryData(queryKey, newData)
}

// Utility to handle unscheduled items
const updateUnscheduledItems = (
  queryClient: QueryClient,
  filters: CalendarFilters,
  operation: 'add' | 'remove',
  item: AlmanacFileCalendarItem,
  previousData: { [key: string]: AlmanacFileCalendarItem[] },
) => {
  const unscheduledQueryKey = calendarKeys.unscheduled(filters)
  const unscheduledItems = queryClient.getQueryData<AlmanacFileCalendarItem[]>(unscheduledQueryKey) || []

  if (operation === 'add') {
    const unscheduledItem = { ...item, post_at: '' }
    updateQueryData(queryClient, unscheduledQueryKey, [...unscheduledItems, unscheduledItem], previousData)
  } else {
    updateQueryData(
      queryClient,
      unscheduledQueryKey,
      unscheduledItems.filter((i) => i.key !== item.key),
      previousData,
    )
  }
}

// Utility to check if a date falls within a range
const isDateInRange = (date: Date, startDate: Date, endDate: Date) => {
  return date >= startDate && date <= endDate
}

// Utility to extract date range from query key
const getDateRangeFromKey = (keyArray: any[]): QueryDateRange | null => {
  const dateRangeObj = keyArray.find((key) => typeof key === 'object' && key !== null && 'dateRange' in key)

  if (dateRangeObj && typeof dateRangeObj.dateRange === 'object') {
    const { startDate, endDate } = dateRangeObj.dateRange
    if (startDate && endDate) {
      return { startDate, endDate }
    }
  }

  return null
}

// Handle rollback of mutations
const handleMutationError = (
  queryClient: QueryClient,
  _error: unknown,
  _variables: unknown,
  context?: MutationContext,
) => {
  if (context?.previousData) {
    Object.entries(context.previousData).forEach(([keyString, data]) => {
      const queryKey = JSON.parse(keyString)
      queryClient.setQueryData(queryKey, data)
    })
  }
}

// Common mutation setup
const setupMutation = async (queryClient: QueryClient, filters: CalendarFilters) => {
  await queryClient.cancelQueries(calendarKeys.filters(filters))
  return { previousData: {} as { [key: string]: AlmanacFileCalendarItem[] } }
}

// Utility to process date range queries
const processDateRangeQueries = (
  queryClient: QueryClient,
  filters: CalendarFilters,

  process: (
    queryKey: QueryKey,
    data: AlmanacFileCalendarItem[],
    dateRange: QueryDateRange,
  ) => AlmanacFileCalendarItem[] | null,
  previousData: { [key: string]: AlmanacFileCalendarItem[] },
) => {
  const queries = queryClient.getQueriesData<AlmanacFileCalendarItem[]>(calendarKeys.filters(filters))

  queries.forEach(([queryKey, data = []]) => {
    const keyArray = queryKey as unknown as any[]
    const dateRange = getDateRangeFromKey(keyArray)

    if (!dateRange) return

    const newData = process(queryKey, data, dateRange)
    if (newData !== null) {
      updateQueryData(queryClient, queryKey, newData, previousData)
    }
  })
}

// Specific processors for different mutation types
const createItemProcessor = (newItem: AlmanacFileCalendarItem) => {
  return (_queryKey: QueryKey, data: AlmanacFileCalendarItem[], dateRange: QueryDateRange) => {
    const createdDate = dayjs(newItem.post_at).toDate()
    const startDate = new Date(dateRange.startDate)
    const endDate = new Date(dateRange.endDate)

    if (isDateInRange(createdDate, startDate, endDate)) {
      return [...data, newItem]
    }
    return null
  }
}

const deleteItemProcessor = (deletedItem: AlmanacFileCalendarItem) => {
  return (_queryKey: QueryKey, data: AlmanacFileCalendarItem[]) => {
    return data.filter((item) => item.key !== deletedItem.key)
  }
}

const updateItemProcessor = (updatedItem: AlmanacFileCalendarItem, originalPostAt: string | null) => {
  return (_queryKey: QueryKey, data: AlmanacFileCalendarItem[], dateRange: QueryDateRange) => {
    const originalDate = originalPostAt ? dayjs(originalPostAt).toDate() : dayjs(updatedItem.post_at).toDate()
    const updatedDate = dayjs(updatedItem.post_at).toDate()
    const startDate = new Date(dateRange.startDate)
    const endDate = new Date(dateRange.endDate)

    const wasInRange = isDateInRange(originalDate, startDate, endDate)
    const isInRange = isDateInRange(updatedDate, startDate, endDate)

    if (wasInRange && isInRange) {
      return data.map((item) => (item.key === updatedItem.key ? updatedItem : item))
    } else if (wasInRange && !isInRange) {
      return data.filter((item) => item.key !== updatedItem.key)
    } else if (!wasInRange && isInRange) {
      return [...data, updatedItem]
    }
    return null
  }
}

const removeItemFromCalendarCache = (
  queryClient: QueryClient,
  itemKey: string,
  options: { invalidateQueries?: boolean } = { invalidateQueries: true },
) => {
  removeItemFromSchedulesCache(queryClient, itemKey, options)
  const queries = queryClient.getQueriesData<AlmanacFileCalendarItem[]>(calendarKeys.all)

  queries.forEach(([queryKey, data]) => {
    if (!data) return

    if (!Array.isArray(data)) {
      if (!Array.isArray(data['previous_files']) || !Array.isArray(data['next_files'])) return

      const newPreviousData = (data['previous_files'] as AlmanacFileCalendarItem[]).filter(
        (item) => item.key !== itemKey,
      )
      const newNextData = (data['next_files'] as AlmanacFileCalendarItem[]).filter((item) => item.key !== itemKey)

      queryClient.setQueryData(queryKey, {
        almanac_file: data['almanac_file'],
        previous_files: newPreviousData,
        next_files: newNextData,
      })
      return
    }

    const newData = data.filter((item) => item.key !== itemKey)

    if (newData.length !== data.length) {
      queryClient.setQueryData(queryKey, newData)
    }
  })

  if (options?.invalidateQueries) {
    queryClient.invalidateQueries(calendarKeys.all, {
      refetchActive: false,
      refetchInactive: false,
    })
  }
}

const removeItemFromSchedulesCache = (
  queryClient: QueryClient,
  itemKey: string,
  options: { invalidateQueries?: boolean } = { invalidateQueries: true },
) => {
  const schedulesQueryKey = ['schedules']
  const queries = queryClient.getQueriesData<BrandPlanSchedule[]>(schedulesQueryKey)

  queries.forEach(([queryKey, data]) => {
    if (!data || !Array.isArray(data)) return

    const newData = data.map((item) => item.contents.filter((content) => content.almanac_file?.key !== itemKey))

    if (newData.length !== data.length) {
      queryClient.setQueryData(queryKey, newData)
    }
  })

  if (options?.invalidateQueries) {
    queryClient.invalidateQueries(schedulesQueryKey, {
      refetchActive: false,
      refetchInactive: false,
    })
  }
}

export const updateCalendarItemInCache = (
  queryClient: QueryClient,
  itemKey: string,
  updater: (item: AlmanacFileCalendarItem | AlmanacFileIndexItem) => AlmanacFileCalendarItem | AlmanacFileIndexItem,
  workspaceId?: number,
): boolean => {
  const calendarQueries = queryClient.getQueriesData<AlmanacFileCalendarItem[]>(calendarKeys.all)
  const fileQueries = workspaceId ? queryClient.getQueriesData<AlmanacFileIndexItem[]>(filesKeys.all(workspaceId)) : []
  let updatedAnyCache = false

  const queries = [...calendarQueries, ...fileQueries]

  queries.forEach(([queryKey, data]) => {
    if (!data) return
    if (!Array.isArray(data)) {
      if ('key' in data && data['key'] === itemKey) {
        queryClient.setQueryData(queryKey, updater(data))
        updatedAnyCache = true
      }
      return
    }

    let updatedThisCache = false
    const newData = data.map((item) => {
      if (item.key === itemKey) {
        updatedThisCache = true
        return updater(item)
      }
      return item
    })

    // Only update the cache if the item was found and updated
    if (updatedThisCache) {
      queryClient.setQueryData(queryKey, newData)
      updatedAnyCache = true
    }
  })

  return updatedAnyCache
}

export {
  setupMutation,
  handleMutationError,
  updateUnscheduledItems,
  processDateRangeQueries,
  createItemProcessor,
  deleteItemProcessor,
  updateItemProcessor,
  removeItemFromCalendarCache,
  removeItemFromSchedulesCache,
  type MutationContext,
}
